import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useModules } from '@/contexts/ModuleContext';
import ModuleGate from '@/components/ModuleGate';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  TrendingUp, Loader2, Trash2, Clock, Calculator, DollarSign, 
  AlertTriangle, CheckCircle2, XCircle, Target, BarChart3,
  FileText, ChevronRight, Sparkles, Building2, Users, Plus, X,
  Shield, Calendar, ArrowRight, Lightbulb, ListChecks, Scale
} from 'lucide-react';
import { toast } from 'sonner';

const AIBusinessCase = ({ user, onLogout }) => {
  const { checkModuleAccess } = useModules();
  const moduleAccess = checkModuleAccess('ai_business_case');

  // Form state - Extended
  const [formData, setFormData] = useState({
    project_name: '',
    project_description: '',
    problem_statement: '',
    strategic_alignment: '',
    implementation_months: '3',
    // One-time costs
    initial_investment: '',
    external_services: '',
    training_costs: '',
    // Personnel costs
    personnel_costs: [],
    // Ongoing costs
    monthly_license_costs: '',
    monthly_maintenance: '',
    monthly_support_costs: '',
    // Benefits - Quantifiable
    monthly_cost_savings: '',
    fte_savings: '',
    fte_hourly_rate: '80',
    productivity_gain_percent: '',
    affected_employees: '',
    monthly_revenue_increase: '',
    // Benefits - Qualitative
    quality_improvements: '',
    compliance_benefits: '',
    customer_satisfaction: '',
    employee_satisfaction: '',
    // Risks
    known_risks: [],
    dependencies: '',
    project_id: ''
  });

  // Personnel entry form
  const [newPersonnel, setNewPersonnel] = useState({ role: '', fte: '', hourly_rate: '', duration_months: '' });
  // Risk entry form
  const [newRisk, setNewRisk] = useState({ description: '', category: 'general' });

  // Results state
  const [businessCases, setBusinessCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertSettings, setConvertSettings] = useState({ department_id: '', start_date: '' });
  const [caseToDelete, setCaseToDelete] = useState(null);
  const [activeFormTab, setActiveFormTab] = useState('basics');

  useEffect(() => {
    fetchBusinessCases();
    fetchProjects();
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchBusinessCases = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/business-case`);
      setBusinessCases(response.data);
    } catch (error) {
      console.error('Error fetching business cases:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const addPersonnel = () => {
    if (newPersonnel.role && newPersonnel.fte && newPersonnel.hourly_rate) {
      setFormData(prev => ({
        ...prev,
        personnel_costs: [...prev.personnel_costs, {
          role: newPersonnel.role,
          fte: parseFloat(newPersonnel.fte),
          hourly_rate: parseFloat(newPersonnel.hourly_rate),
          duration_months: parseInt(newPersonnel.duration_months) || parseInt(formData.implementation_months) || 3
        }]
      }));
      setNewPersonnel({ role: '', fte: '', hourly_rate: '', duration_months: '' });
    }
  };

  const removePersonnel = (index) => {
    setFormData(prev => ({
      ...prev,
      personnel_costs: prev.personnel_costs.filter((_, i) => i !== index)
    }));
  };

  const addRisk = () => {
    if (newRisk.description) {
      setFormData(prev => ({
        ...prev,
        known_risks: [...prev.known_risks, { ...newRisk }]
      }));
      setNewRisk({ description: '', category: 'general' });
    }
  };

  const removeRisk = (index) => {
    setFormData(prev => ({
      ...prev,
      known_risks: prev.known_risks.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.project_name || !formData.project_description) {
      toast.error('Bitte Projektname und Beschreibung eingeben');
      return;
    }

    setIsGenerating(true);
    try {
      const payload = {
        ...formData,
        implementation_months: parseInt(formData.implementation_months) || 3,
        initial_investment: parseFloat(formData.initial_investment) || 0,
        external_services: parseFloat(formData.external_services) || 0,
        training_costs: parseFloat(formData.training_costs) || 0,
        monthly_license_costs: parseFloat(formData.monthly_license_costs) || 0,
        monthly_maintenance: parseFloat(formData.monthly_maintenance) || 0,
        monthly_support_costs: parseFloat(formData.monthly_support_costs) || 0,
        monthly_cost_savings: parseFloat(formData.monthly_cost_savings) || 0,
        fte_savings: parseFloat(formData.fte_savings) || 0,
        fte_hourly_rate: parseFloat(formData.fte_hourly_rate) || 80,
        productivity_gain_percent: parseFloat(formData.productivity_gain_percent) || 0,
        affected_employees: parseInt(formData.affected_employees) || 0,
        monthly_revenue_increase: parseFloat(formData.monthly_revenue_increase) || 0,
        project_id: formData.project_id || null
      };

      const response = await axios.post(`${API}/business-case`, payload);
      setSelectedCase(response.data);
      
      const listItem = {
        ...response.data,
        roi_percentage: response.data.financial_summary?.roi_percentage || response.data.roi_analysis?.summary_3_years?.roi_percentage
      };
      setBusinessCases(prev => [listItem, ...prev]);
      toast.success('Business Case wurde erstellt');
      
      // Reset form
      setFormData({
        project_name: '', project_description: '', problem_statement: '', strategic_alignment: '',
        implementation_months: '3', initial_investment: '', external_services: '', training_costs: '',
        personnel_costs: [], monthly_license_costs: '', monthly_maintenance: '', monthly_support_costs: '',
        monthly_cost_savings: '', fte_savings: '', fte_hourly_rate: '80', productivity_gain_percent: '',
        affected_employees: '', monthly_revenue_increase: '', quality_improvements: '', compliance_benefits: '',
        customer_satisfaction: '', employee_satisfaction: '', known_risks: [], dependencies: '', project_id: ''
      });
    } catch (error) {
      console.error('Error creating business case:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Business Case');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!caseToDelete) return;
    try {
      await axios.delete(`${API}/business-case/${caseToDelete.id}`);
      setBusinessCases(prev => prev.filter(c => c.id !== caseToDelete.id));
      if (selectedCase?.id === caseToDelete.id) setSelectedCase(null);
      toast.success('Business Case wurde gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setShowDeleteDialog(false);
      setCaseToDelete(null);
    }
  };

  const loadCase = async (caseId) => {
    try {
      const response = await axios.get(`${API}/business-case/${caseId}`);
      setSelectedCase(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden des Business Case');
    }
  };

  const handleConvertToProject = async () => {
    if (!selectedCase) return;
    
    setIsConverting(true);
    try {
      const payload = {
        department_id: convertSettings.department_id || null,
        start_date: convertSettings.start_date || null
      };
      
      const response = await axios.post(`${API}/business-case/${selectedCase.id}/convert-to-project`, payload);
      
      toast.success(`Projekt "${response.data.project?.name}" wurde mit ${response.data.tasks_created} Tasks erstellt`);
      
      // Refresh the business cases list and reload selected case
      await fetchBusinessCases();
      await loadCase(selectedCase.id);
      
      setShowConvertDialog(false);
      setConvertSettings({ department_id: '', start_date: '' });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler bei der Konvertierung';
      toast.error(errorMsg);
    } finally {
      setIsConverting(false);
    }
  };

  const getRecommendationColor = (rec) => {
    if (!rec) return 'bg-gray-100 text-gray-700';
    if (rec.includes('NICHT')) return 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400';
    if (rec.includes('BEDINGT')) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400';
    return 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400';
  };

  const getRecommendationIcon = (rec) => {
    if (!rec) return <Target className="w-5 h-5" />;
    if (rec.includes('NICHT')) return <XCircle className="w-5 h-5" />;
    if (rec.includes('BEDINGT')) return <AlertTriangle className="w-5 h-5" />;
    return <CheckCircle2 className="w-5 h-5" />;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(value || 0);
  };

  const getRiskColor = (likelihood, impact) => {
    const score = { 'Niedrig': 1, 'Mittel': 2, 'Hoch': 3 };
    const total = (score[likelihood] || 2) * (score[impact] || 2);
    if (total >= 6) return 'bg-red-100 border-red-300 dark:bg-red-500/20';
    if (total >= 4) return 'bg-yellow-100 border-yellow-300 dark:bg-yellow-500/20';
    return 'bg-green-100 border-green-300 dark:bg-green-500/20';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <ModuleGate moduleId="ai_business_case">
        <div className="container mx-auto p-6 max-w-7xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-primary" />
                KI-Business Case Generator
              </h1>
              <p className="text-muted-foreground mt-1">
                Erstellen Sie professionelle Business Cases für die Geschäftsleitung
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Form & History */}
            <div className="lg:col-span-1 space-y-6">
              {/* New Business Case Form */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Neuer Business Case
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit}>
                    <Tabs value={activeFormTab} onValueChange={setActiveFormTab}>
                      <TabsList className="grid grid-cols-4 w-full mb-4">
                        <TabsTrigger value="basics" className="text-xs">Basis</TabsTrigger>
                        <TabsTrigger value="costs" className="text-xs">Kosten</TabsTrigger>
                        <TabsTrigger value="benefits" className="text-xs">Nutzen</TabsTrigger>
                        <TabsTrigger value="risks" className="text-xs">Risiken</TabsTrigger>
                      </TabsList>

                      {/* Tab: Basics */}
                      <TabsContent value="basics" className="space-y-4 mt-0">
                        <div className="space-y-2">
                          <Label>Projektname *</Label>
                          <Input
                            value={formData.project_name}
                            onChange={(e) => setFormData({...formData, project_name: e.target.value})}
                            placeholder="z.B. ERP-System Einführung"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Problemstellung / Ausgangslage *</Label>
                          <Textarea
                            value={formData.problem_statement}
                            onChange={(e) => setFormData({...formData, problem_statement: e.target.value})}
                            placeholder="Welches Problem soll gelöst werden? Was ist die aktuelle Situation?"
                            rows={3}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Projektbeschreibung / Lösung *</Label>
                          <Textarea
                            value={formData.project_description}
                            onChange={(e) => setFormData({...formData, project_description: e.target.value})}
                            placeholder="Was ist die vorgeschlagene Lösung?"
                            rows={3}
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Strategische Ausrichtung</Label>
                          <Textarea
                            value={formData.strategic_alignment}
                            onChange={(e) => setFormData({...formData, strategic_alignment: e.target.value})}
                            placeholder="Wie trägt das Projekt zur Unternehmensstrategie bei?"
                            rows={2}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label>Implementierung (Monate)</Label>
                            <Input
                              type="number"
                              value={formData.implementation_months}
                              onChange={(e) => setFormData({...formData, implementation_months: e.target.value})}
                            />
                          </div>
                          {projects.length > 0 && (
                            <div className="space-y-2">
                              <Label>Mit Projekt verknüpfen</Label>
                              <Select 
                                value={formData.project_id || "none"} 
                                onValueChange={(val) => setFormData({...formData, project_id: val === "none" ? "" : val})}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Optional" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Keines</SelectItem>
                                  {projects.map(p => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </TabsContent>

                      {/* Tab: Costs */}
                      <TabsContent value="costs" className="space-y-4 mt-0">
                        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 space-y-3">
                          <Label className="text-red-700 dark:text-red-400 font-semibold">Einmalige Kosten</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Investition (CHF)</Label>
                              <Input type="number" value={formData.initial_investment}
                                onChange={(e) => setFormData({...formData, initial_investment: e.target.value})}
                                placeholder="Hardware, Lizenzen..." />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Externe Dienstleister</Label>
                              <Input type="number" value={formData.external_services}
                                onChange={(e) => setFormData({...formData, external_services: e.target.value})}
                                placeholder="Berater, Vendors..." />
                            </div>
                            <div className="col-span-2 space-y-1">
                              <Label className="text-xs">Schulung & Change Mgmt</Label>
                              <Input type="number" value={formData.training_costs}
                                onChange={(e) => setFormData({...formData, training_costs: e.target.value})}
                                placeholder="Trainings, Workshops..." />
                            </div>
                          </div>
                        </div>

                        {/* Personnel Costs */}
                        <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-500/10 space-y-3">
                          <Label className="text-orange-700 dark:text-orange-400 font-semibold flex items-center gap-2">
                            <Users className="w-4 h-4" /> Personalkosten (Implementierung)
                          </Label>
                          
                          {formData.personnel_costs.length > 0 && (
                            <div className="space-y-1">
                              {formData.personnel_costs.map((p, i) => (
                                <div key={i} className="flex items-center justify-between text-xs bg-white dark:bg-gray-800 p-2 rounded">
                                  <span>{p.role}: {p.fte} FTE × CHF {p.hourly_rate}/h × {p.duration_months} Mt.</span>
                                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removePersonnel(i)}>
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="grid grid-cols-4 gap-1">
                            <Input placeholder="Rolle" value={newPersonnel.role} className="text-xs"
                              onChange={(e) => setNewPersonnel({...newPersonnel, role: e.target.value})} />
                            <Input placeholder="FTE" type="number" step="0.1" value={newPersonnel.fte} className="text-xs"
                              onChange={(e) => setNewPersonnel({...newPersonnel, fte: e.target.value})} />
                            <Input placeholder="CHF/h" type="number" value={newPersonnel.hourly_rate} className="text-xs"
                              onChange={(e) => setNewPersonnel({...newPersonnel, hourly_rate: e.target.value})} />
                            <Button type="button" size="sm" onClick={addPersonnel} className="text-xs">
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>

                        {/* Ongoing Costs */}
                        <div className="p-3 rounded-lg bg-red-50/50 dark:bg-red-500/5 space-y-3">
                          <Label className="text-red-600 dark:text-red-400 font-semibold">Laufende Kosten (monatlich)</Label>
                          <div className="grid grid-cols-3 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Lizenzen</Label>
                              <Input type="number" value={formData.monthly_license_costs}
                                onChange={(e) => setFormData({...formData, monthly_license_costs: e.target.value})} />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Wartung</Label>
                              <Input type="number" value={formData.monthly_maintenance}
                                onChange={(e) => setFormData({...formData, monthly_maintenance: e.target.value})} />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Support</Label>
                              <Input type="number" value={formData.monthly_support_costs}
                                onChange={(e) => setFormData({...formData, monthly_support_costs: e.target.value})} />
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      {/* Tab: Benefits */}
                      <TabsContent value="benefits" className="space-y-4 mt-0">
                        <div className="p-3 rounded-lg bg-green-50 dark:bg-green-500/10 space-y-3">
                          <Label className="text-green-700 dark:text-green-400 font-semibold">Quantifizierbarer Nutzen (monatlich)</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Direkte Einsparungen (CHF)</Label>
                              <Input type="number" value={formData.monthly_cost_savings}
                                onChange={(e) => setFormData({...formData, monthly_cost_savings: e.target.value})}
                                placeholder="Reduktion laufender Kosten" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Umsatzsteigerung (CHF)</Label>
                              <Input type="number" value={formData.monthly_revenue_increase}
                                onChange={(e) => setFormData({...formData, monthly_revenue_increase: e.target.value})} />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">FTE-Einsparung</Label>
                              <Input type="number" step="0.1" value={formData.fte_savings}
                                onChange={(e) => setFormData({...formData, fte_savings: e.target.value})}
                                placeholder="z.B. 0.5" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Stundensatz (CHF)</Label>
                              <Input type="number" value={formData.fte_hourly_rate}
                                onChange={(e) => setFormData({...formData, fte_hourly_rate: e.target.value})} />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Produktivität +%</Label>
                              <Input type="number" value={formData.productivity_gain_percent}
                                onChange={(e) => setFormData({...formData, productivity_gain_percent: e.target.value})}
                                placeholder="z.B. 10" />
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <Label className="text-xs">Betroffene Mitarbeiter (für Produktivität)</Label>
                            <Input type="number" value={formData.affected_employees}
                              onChange={(e) => setFormData({...formData, affected_employees: e.target.value})} />
                          </div>
                        </div>

                        <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-500/10 space-y-3">
                          <Label className="text-blue-700 dark:text-blue-400 font-semibold">Qualitativer Nutzen</Label>
                          <div className="space-y-2">
                            <Textarea placeholder="Qualitätsverbesserungen..." rows={2}
                              value={formData.quality_improvements}
                              onChange={(e) => setFormData({...formData, quality_improvements: e.target.value})} />
                            <Textarea placeholder="Compliance & Regulatorik..." rows={2}
                              value={formData.compliance_benefits}
                              onChange={(e) => setFormData({...formData, compliance_benefits: e.target.value})} />
                            <Textarea placeholder="Kundenzufriedenheit..." rows={2}
                              value={formData.customer_satisfaction}
                              onChange={(e) => setFormData({...formData, customer_satisfaction: e.target.value})} />
                          </div>
                        </div>
                      </TabsContent>

                      {/* Tab: Risks */}
                      <TabsContent value="risks" className="space-y-4 mt-0">
                        <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-500/10 space-y-3">
                          <Label className="text-orange-700 dark:text-orange-400 font-semibold flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" /> Bekannte Risiken
                          </Label>
                          
                          {formData.known_risks.length > 0 && (
                            <div className="space-y-1">
                              {formData.known_risks.map((r, i) => (
                                <div key={i} className="flex items-center justify-between text-xs bg-white dark:bg-gray-800 p-2 rounded">
                                  <span className="flex-1">{r.description} <Badge variant="outline" className="ml-1 text-xs">{r.category}</Badge></span>
                                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeRisk(i)}>
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="flex gap-2">
                            <Input placeholder="Risiko beschreiben..." value={newRisk.description}
                              onChange={(e) => setNewRisk({...newRisk, description: e.target.value})} className="flex-1 text-xs" />
                            <Select value={newRisk.category} onValueChange={(val) => setNewRisk({...newRisk, category: val})}>
                              <SelectTrigger className="w-28 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="technical">Technisch</SelectItem>
                                <SelectItem value="organizational">Organisation</SelectItem>
                                <SelectItem value="financial">Finanziell</SelectItem>
                                <SelectItem value="external">Extern</SelectItem>
                                <SelectItem value="general">Allgemein</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button type="button" size="sm" onClick={addRisk}><Plus className="w-3 h-3" /></Button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Abhängigkeiten</Label>
                          <Textarea value={formData.dependencies}
                            onChange={(e) => setFormData({...formData, dependencies: e.target.value})}
                            placeholder="Externe Abhängigkeiten, andere Projekte, Lieferanten..."
                            rows={3} />
                        </div>
                      </TabsContent>
                    </Tabs>

                    <Button type="submit" className="w-full mt-4" disabled={isGenerating}>
                      {isGenerating ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyse wird erstellt (30-60s)...</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" />Business Case erstellen</>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* History */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Bisherige Analysen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : businessCases.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Noch keine Business Cases erstellt
                    </p>
                  ) : (
                    <ScrollArea className="h-[300px]">
                      <div className="space-y-2 pr-4">
                        {businessCases.map(bc => (
                          <div
                            key={bc.id}
                            className={`p-3 rounded-lg border cursor-pointer transition-colors hover:bg-muted/50 ${
                              selectedCase?.id === bc.id ? 'border-primary bg-muted/50' : ''
                            }`}
                            onClick={() => loadCase(bc.id)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm truncate">{bc.project_name}</h4>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <Badge className={`text-xs ${getRecommendationColor(bc.recommendation_short)}`}>
                                    {bc.recommendation_short || 'Ausstehend'}
                                  </Badge>
                                  {(bc.roi_percentage !== null && bc.roi_percentage !== undefined) && (
                                    <span className="text-xs text-muted-foreground">
                                      ROI: {bc.roi_percentage?.toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                                {bc.total_investment && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Investition: {formatCurrency(bc.total_investment)}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  {new Date(bc.created_at).toLocaleDateString('de-CH')}
                                </p>
                              </div>
                              <Button variant="ghost" size="icon" className="h-8 w-8"
                                onClick={(e) => { e.stopPropagation(); setCaseToDelete(bc); setShowDeleteDialog(true); }}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column: Results */}
            <div className="lg:col-span-2">
              {selectedCase ? (
                <div className="space-y-4">
                  {/* Recommendation Header */}
                  <Card className={`${getRecommendationColor(selectedCase.recommendation_short)} border-0`}>
                    <CardContent className="py-4">
                      <div className="flex items-center gap-3">
                        {getRecommendationIcon(selectedCase.recommendation_short)}
                        <div className="flex-1">
                          <h2 className="font-bold text-lg">{selectedCase.project_name}</h2>
                          <p className="text-sm opacity-80">
                            Empfehlung: {selectedCase.recommendation_short || 'Ausstehend'}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {new Date(selectedCase.created_at).toLocaleDateString('de-CH')}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>

                  {/* KPIs */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <Card>
                      <CardContent className="pt-3 pb-2">
                        <div className="text-xl font-bold text-primary">
                          {selectedCase.financial_summary?.roi_percentage?.toFixed(1) || selectedCase.roi_analysis?.summary_3_years?.roi_percentage?.toFixed(1) || '0'}%
                        </div>
                        <p className="text-xs text-muted-foreground">ROI (3 Jahre)</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-3 pb-2">
                        <div className="text-xl font-bold">
                          {selectedCase.payback_period_months ? `${selectedCase.payback_period_months.toFixed(0)} Mt.` : 'N/A'}
                        </div>
                        <p className="text-xs text-muted-foreground">Amortisation</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-3 pb-2">
                        <div className="text-xl font-bold text-red-600">
                          {formatCurrency(selectedCase.financial_summary?.total_investment || selectedCase.roi_analysis?.summary_3_years?.total_investment)}
                        </div>
                        <p className="text-xs text-muted-foreground">Investition</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-3 pb-2">
                        <div className="text-xl font-bold text-green-600">
                          {formatCurrency(selectedCase.financial_summary?.total_benefits || selectedCase.roi_analysis?.summary_3_years?.total_benefits)}
                        </div>
                        <p className="text-xs text-muted-foreground">Nutzen (3J)</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-3 pb-2">
                        <div className={`text-xl font-bold ${(selectedCase.financial_summary?.npv || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(selectedCase.financial_summary?.npv || selectedCase.roi_analysis?.summary_3_years?.npv)}
                        </div>
                        <p className="text-xs text-muted-foreground">NPV</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Detailed Analysis Tabs */}
                  <Card>
                    <Tabs defaultValue="summary">
                      <CardHeader className="pb-0">
                        <TabsList className="grid grid-cols-6 w-full">
                          <TabsTrigger value="summary" className="text-xs">Summary</TabsTrigger>
                          <TabsTrigger value="options" className="text-xs">Optionen</TabsTrigger>
                          <TabsTrigger value="financials" className="text-xs">Finanzen</TabsTrigger>
                          <TabsTrigger value="risks" className="text-xs">Risiken</TabsTrigger>
                          <TabsTrigger value="implementation" className="text-xs">Umsetzung</TabsTrigger>
                          <TabsTrigger value="decision" className="text-xs">Entscheidung</TabsTrigger>
                        </TabsList>
                      </CardHeader>
                      <CardContent className="pt-4">
                        {/* Executive Summary */}
                        <TabsContent value="summary" className="mt-0 space-y-4">
                          <div>
                            <h4 className="font-semibold mb-2 flex items-center gap-2">
                              <FileText className="w-4 h-4" /> Executive Summary
                            </h4>
                            <p className="text-sm leading-relaxed whitespace-pre-wrap bg-muted/50 p-4 rounded-lg">
                              {selectedCase.executive_summary}
                            </p>
                          </div>
                          
                          {selectedCase.problem_and_context && (
                            <div>
                              <h4 className="font-semibold mb-2 flex items-center gap-2">
                                <Lightbulb className="w-4 h-4" /> Problem & Kontext
                              </h4>
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                {selectedCase.problem_and_context}
                              </p>
                            </div>
                          )}
                          
                          {selectedCase.strategic_alignment_analysis && (
                            <div>
                              <h4 className="font-semibold mb-2 flex items-center gap-2">
                                <Target className="w-4 h-4" /> Strategische Ausrichtung
                              </h4>
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                {selectedCase.strategic_alignment_analysis}
                              </p>
                            </div>
                          )}
                        </TabsContent>

                        {/* Options Analysis */}
                        <TabsContent value="options" className="mt-0">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <Scale className="w-4 h-4" /> Optionsanalyse
                          </h4>
                          {selectedCase.options_analysis && selectedCase.options_analysis.length > 0 ? (
                            <div className="space-y-3">
                              {selectedCase.options_analysis.map((opt, i) => (
                                <div key={i} className={`p-4 rounded-lg border ${opt.recommendation ? 'border-green-500 bg-green-50 dark:bg-green-500/10' : 'bg-muted/30'}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="font-medium flex items-center gap-2">
                                      Option {opt.option_number}: {opt.name}
                                      {opt.recommendation && <Badge className="bg-green-600">Empfohlen</Badge>}
                                    </h5>
                                    <Badge variant="outline">{opt.feasibility}</Badge>
                                  </div>
                                  <p className="text-sm text-muted-foreground mb-3">{opt.description}</p>
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <span className="text-green-600 font-medium">Vorteile:</span>
                                      <ul className="list-disc list-inside mt-1">
                                        {opt.pros?.map((p, j) => <li key={j} className="text-xs">{p}</li>)}
                                      </ul>
                                    </div>
                                    <div>
                                      <span className="text-red-600 font-medium">Nachteile:</span>
                                      <ul className="list-disc list-inside mt-1">
                                        {opt.cons?.map((c, j) => <li key={j} className="text-xs">{c}</li>)}
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">Keine Optionsanalyse verfügbar</p>
                          )}
                        </TabsContent>

                        {/* Financials */}
                        <TabsContent value="financials" className="mt-0 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Costs Breakdown */}
                            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-500/10">
                              <h5 className="font-semibold text-red-700 dark:text-red-400 mb-3">Kostenübersicht</h5>
                              <div className="space-y-2 text-sm">
                                <div className="font-medium border-b pb-1">Einmalige Kosten</div>
                                {selectedCase.roi_analysis?.one_time_costs && (
                                  <>
                                    <div className="flex justify-between">
                                      <span>Investition:</span>
                                      <span>{formatCurrency(selectedCase.roi_analysis.one_time_costs.initial_investment)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Externe Dienstleister:</span>
                                      <span>{formatCurrency(selectedCase.roi_analysis.one_time_costs.external_services)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Schulung:</span>
                                      <span>{formatCurrency(selectedCase.roi_analysis.one_time_costs.training_costs)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Personal:</span>
                                      <span>{formatCurrency(selectedCase.roi_analysis.one_time_costs.personnel_costs)}</span>
                                    </div>
                                    <div className="flex justify-between font-bold border-t pt-1">
                                      <span>Gesamt einmalig:</span>
                                      <span>{formatCurrency(selectedCase.roi_analysis.one_time_costs.total)}</span>
                                    </div>
                                  </>
                                )}
                                <div className="font-medium border-b pb-1 mt-3">Laufende Kosten (monatlich)</div>
                                {selectedCase.roi_analysis?.ongoing_monthly_costs && (
                                  <>
                                    <div className="flex justify-between">
                                      <span>Lizenzen:</span>
                                      <span>{formatCurrency(selectedCase.roi_analysis.ongoing_monthly_costs.licenses)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Wartung:</span>
                                      <span>{formatCurrency(selectedCase.roi_analysis.ongoing_monthly_costs.maintenance)}</span>
                                    </div>
                                    <div className="flex justify-between font-bold border-t pt-1">
                                      <span>Gesamt monatlich:</span>
                                      <span>{formatCurrency(selectedCase.roi_analysis.ongoing_monthly_costs.total)}</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                            
                            {/* Benefits Breakdown */}
                            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-500/10">
                              <h5 className="font-semibold text-green-700 dark:text-green-400 mb-3">Nutzenübersicht (monatlich)</h5>
                              {selectedCase.roi_analysis?.monthly_benefits && (
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span>Direkte Einsparungen:</span>
                                    <span>{formatCurrency(selectedCase.roi_analysis.monthly_benefits.cost_savings)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>FTE-Einsparungen:</span>
                                    <span>{formatCurrency(selectedCase.roi_analysis.monthly_benefits.fte_savings)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Produktivitätsgewinn:</span>
                                    <span>{formatCurrency(selectedCase.roi_analysis.monthly_benefits.productivity_gains)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Umsatzsteigerung:</span>
                                    <span>{formatCurrency(selectedCase.roi_analysis.monthly_benefits.revenue_increase)}</span>
                                  </div>
                                  <div className="flex justify-between font-bold border-t pt-1">
                                    <span>Gesamt monatlich:</span>
                                    <span>{formatCurrency(selectedCase.roi_analysis.monthly_benefits.total)}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Year by Year */}
                          {selectedCase.roi_analysis?.yearly_breakdown && (
                            <div>
                              <h5 className="font-semibold mb-3">Jahr-für-Jahr Entwicklung</h5>
                              <div className="space-y-2">
                                {selectedCase.roi_analysis.yearly_breakdown.map((year) => (
                                  <div key={year.year} className="p-3 rounded-lg bg-muted/50">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-medium">Jahr {year.year}</span>
                                      <span className={`font-bold ${year.cumulative >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        Kumuliert: {formatCurrency(year.cumulative)}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 text-sm">
                                      <div><span className="text-muted-foreground">Kosten:</span> <span className="text-red-600">{formatCurrency(year.costs)}</span></div>
                                      <div><span className="text-muted-foreground">Nutzen:</span> <span className="text-green-600">{formatCurrency(year.benefits)}</span></div>
                                      <div><span className="text-muted-foreground">Netto:</span> <span className={year.net >= 0 ? 'text-green-600' : 'text-red-600'}>{formatCurrency(year.net)}</span></div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Cost-Benefit Detail */}
                          {selectedCase.cost_benefit_detail && (
                            <div className="space-y-3">
                              {selectedCase.cost_benefit_detail.cost_analysis && (
                                <div className="p-3 rounded-lg border">
                                  <h5 className="font-medium mb-2">Kostenanalyse</h5>
                                  <p className="text-sm text-muted-foreground">{selectedCase.cost_benefit_detail.cost_analysis}</p>
                                </div>
                              )}
                              {selectedCase.cost_benefit_detail.benefit_analysis && (
                                <div className="p-3 rounded-lg border">
                                  <h5 className="font-medium mb-2">Nutzenanalyse</h5>
                                  <p className="text-sm text-muted-foreground">{selectedCase.cost_benefit_detail.benefit_analysis}</p>
                                </div>
                              )}
                              {selectedCase.cost_benefit_detail.sensitivity_note && (
                                <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200">
                                  <h5 className="font-medium mb-2 text-yellow-700 dark:text-yellow-400">Sensitivität</h5>
                                  <p className="text-sm">{selectedCase.cost_benefit_detail.sensitivity_note}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </TabsContent>

                        {/* Risks */}
                        <TabsContent value="risks" className="mt-0">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <Shield className="w-4 h-4" /> Risikobewertung
                          </h4>
                          {selectedCase.risk_assessment && selectedCase.risk_assessment.length > 0 ? (
                            <div className="space-y-2">
                              {selectedCase.risk_assessment.map((risk, i) => (
                                <div key={i} className={`p-3 rounded-lg border ${getRiskColor(risk.likelihood, risk.impact)}`}>
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <Badge variant="outline" className="text-xs">{risk.id || `R${i+1}`}</Badge>
                                        <Badge variant="outline" className="text-xs">{risk.category}</Badge>
                                      </div>
                                      <p className="font-medium text-sm">{risk.description}</p>
                                    </div>
                                    <div className="text-right text-xs">
                                      <div>W: <span className="font-medium">{risk.likelihood}</span></div>
                                      <div>A: <span className="font-medium">{risk.impact}</span></div>
                                    </div>
                                  </div>
                                  <div className="mt-2 pt-2 border-t">
                                    <span className="text-xs font-medium">Massnahme: </span>
                                    <span className="text-xs text-muted-foreground">{risk.mitigation}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">Keine Risikobewertung verfügbar</p>
                          )}
                        </TabsContent>

                        {/* Implementation */}
                        <TabsContent value="implementation" className="mt-0">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <ListChecks className="w-4 h-4" /> Implementierungsplan
                          </h4>
                          {selectedCase.implementation_plan && selectedCase.implementation_plan.length > 0 ? (
                            <div className="space-y-3">
                              {selectedCase.implementation_plan.map((phase, i) => (
                                <div key={i} className="p-4 rounded-lg border">
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="font-medium">Phase {phase.phase}: {phase.name}</h5>
                                    <Badge variant="outline">{phase.duration_weeks} Wochen</Badge>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                    <div>
                                      <span className="font-medium text-muted-foreground">Aktivitäten:</span>
                                      <ul className="list-disc list-inside mt-1">
                                        {phase.key_activities?.map((a, j) => <li key={j} className="text-xs">{a}</li>)}
                                      </ul>
                                    </div>
                                    <div>
                                      <span className="font-medium text-muted-foreground">Ergebnisse:</span>
                                      <ul className="list-disc list-inside mt-1">
                                        {phase.deliverables?.map((d, j) => <li key={j} className="text-xs">{d}</li>)}
                                      </ul>
                                    </div>
                                  </div>
                                  {phase.resources_needed && (
                                    <div className="mt-2 text-xs text-muted-foreground">
                                      <span className="font-medium">Ressourcen: </span>{phase.resources_needed}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">Kein Implementierungsplan verfügbar</p>
                          )}
                        </TabsContent>

                        {/* Decision */}
                        <TabsContent value="decision" className="mt-0 space-y-4">
                          <div className={`p-4 rounded-lg ${getRecommendationColor(selectedCase.recommendation_short)}`}>
                            <div className="flex items-center gap-2 mb-3">
                              {getRecommendationIcon(selectedCase.recommendation_short)}
                              <h4 className="font-bold text-lg">{selectedCase.recommendation_short}</h4>
                            </div>
                            <p className="text-sm whitespace-pre-wrap">{selectedCase.recommendation}</p>
                          </div>
                          
                          {selectedCase.decision_request && (
                            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-200">
                              <h4 className="font-semibold mb-2 flex items-center gap-2 text-blue-700 dark:text-blue-400">
                                <ArrowRight className="w-4 h-4" /> Entscheidungsantrag
                              </h4>
                              <p className="text-sm">{selectedCase.decision_request}</p>
                            </div>
                          )}
                          
                          {/* Convert to Project Button */}
                          <div className="pt-4 border-t">
                            {selectedCase.converted_to_project ? (
                              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                                <CheckCircle2 className="w-5 h-5" />
                                <span className="font-medium">Bereits in Projekt umgewandelt</span>
                                {selectedCase.project_id && (
                                  <a 
                                    href={`/projects/${selectedCase.project_id}`}
                                    className="text-blue-600 hover:underline text-sm ml-2"
                                  >
                                    Zum Projekt →
                                  </a>
                                )}
                              </div>
                            ) : (
                              <Button 
                                data-testid="convert-to-project-btn"
                                onClick={() => setShowConvertDialog(true)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Building2 className="w-4 h-4 mr-2" />
                                Projekt erstellen
                              </Button>
                            )}
                          </div>
                        </TabsContent>
                      </CardContent>
                    </Tabs>
                  </Card>
                </div>
              ) : (
                <Card className="h-full min-h-[500px] flex items-center justify-center">
                  <div className="text-center p-8">
                    <TrendingUp className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                    <h3 className="text-lg font-medium text-muted-foreground mb-2">
                      Kein Business Case ausgewählt
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-md">
                      Erstellen Sie einen neuen Business Case mit dem Formular links oder wählen Sie einen bestehenden aus der Liste.
                    </p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>

        {/* Delete Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Business Case löschen?</DialogTitle>
              <DialogDescription>
                Möchten Sie "{caseToDelete?.project_name}" wirklich löschen?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Abbrechen</Button>
              <Button variant="destructive" onClick={handleDelete}>Löschen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Convert to Project Dialog */}
        <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Projekt aus Business Case erstellen
              </DialogTitle>
              <DialogDescription>
                Der Business Case "{selectedCase?.project_name}" wird in ein neues Projekt mit automatisch erstellten Tasks umgewandelt.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="convert-department">Abteilung (optional)</Label>
                <Select 
                  value={convertSettings.department_id} 
                  onValueChange={(value) => setConvertSettings(prev => ({ ...prev, department_id: value }))}
                >
                  <SelectTrigger id="convert-department">
                    <SelectValue placeholder="Abteilung wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="convert-start-date">Startdatum (optional)</Label>
                <Input
                  id="convert-start-date"
                  type="date"
                  value={convertSettings.start_date}
                  onChange={(e) => setConvertSettings(prev => ({ ...prev, start_date: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">
                  Leer lassen für heutiges Datum
                </p>
              </div>
              
              <div className="p-3 rounded-lg bg-muted/50 text-sm">
                <p className="font-medium mb-1">Was wird erstellt:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Neues Projekt mit Budget aus Business Case</li>
                  <li>Tasks für jede Implementierungsphase</li>
                  <li>Sub-Tasks für Aktivitäten</li>
                  <li>Risiko-Monitoring Tasks</li>
                </ul>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleConvertToProject}
                disabled={isConverting}
                className="bg-green-600 hover:bg-green-700"
              >
                {isConverting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Erstelle Projekt...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Projekt erstellen
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </ModuleGate>
    </Layout>
  );
};

export default AIBusinessCase;
